import { BlotterPanelDocumentStatus, DocumentFormat } from 'epic/common-form/Documentation';
import type { BlotterItemDetails } from 'models/blotters/BlotterItemDetails';
import type { BlotterType } from 'models/blotters/BlotterType';
import { DocumentType } from 'models/documents/DocumentType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { BackendError } from 'models/NextWorkflowError';
import { BlotterPanelIDByBlotterType } from 'state/blotters/blotterPanel/blotterPanel.model';

export type BlotterPanelAction =
  | BlotterPanelItemReset
  | BlotterPanelItemDetailsRequested
  | BlotterPanelItemDetailsFetched
  | BlotterPanelItemDetailsFetchFailed
  | BlotterPanelItemDetailsDismissAlert
  | BlotterPanelItemDetailsSecondarySelected
  | BlotterPanelItemDetailsSelected
  | BlotterPanelPriceImprovementActionsAdded
  | BlotterPanelDownloadStatusUpdated;

export function blotterPanelItemReset() {
  return { type: 'BLOTTER_PANEL_ITEM_RESET' as const };
}
export type BlotterPanelItemReset = ReturnType<typeof blotterPanelItemReset>;
export function blotterPanelItemDetailsRequested<
  T extends IBlotterItem = IBlotterItem,
  Blotter extends BlotterType = BlotterType,
>(id: BlotterPanelIDByBlotterType<Blotter>, blotterType: Blotter, item?: T) {
  return { type: 'BLOTTER_PANEL_ITEM_DETAILS_REQUESTED' as const, id, blotterType, item };
}
export type BlotterPanelItemDetailsRequested = ReturnType<typeof blotterPanelItemDetailsRequested>;

export function blotterPanelItemDetailsFetched<Blotter extends BlotterType = BlotterType>(
  id: BlotterPanelIDByBlotterType<Blotter>,
  blotterType: BlotterType,
  details: BlotterItemDetails,
) {
  return { type: 'BLOTTER_PANEL_ITEM_DETAILS_FETCHED' as const, id, blotterType, details };
}
export type BlotterPanelItemDetailsFetched = ReturnType<typeof blotterPanelItemDetailsFetched>;

export function blotterPanelItemDetailsFetchFailed<Blotter extends BlotterType = BlotterType>(
  id: BlotterPanelIDByBlotterType<Blotter>,
  blotterType: BlotterType,
  details: BlotterItemDetails,
  error?: BackendError,
) {
  return { type: 'BLOTTER_PANEL_ITEM_DETAILS_FETCH_FAILED' as const, id, blotterType, details, error };
}
export type BlotterPanelItemDetailsFetchFailed = ReturnType<typeof blotterPanelItemDetailsFetchFailed>;

export function blotterPanelItemDetailsDismissAlert() {
  return { type: 'BLOTTER_PANEL_ITEM_DETAILS_DISMISS_ALERT' as const };
}
export type BlotterPanelItemDetailsDismissAlert = ReturnType<typeof blotterPanelItemDetailsDismissAlert>;

export function blotterPanelItemDetailsSecondarySelected(quoteId: string) {
  return { type: 'BLOTTER_PANEL_ITEM_SECONDARY_SELECTED' as const, quoteId };
}
export type BlotterPanelItemDetailsSecondarySelected = ReturnType<typeof blotterPanelItemDetailsSecondarySelected>;

export function blotterPanelItemDetailsSelected(id: string, blotterType: BlotterType) {
  return { type: 'BLOTTER_PANEL_ITEM_SELECTED' as const, id, blotterType };
}
export type BlotterPanelItemDetailsSelected = ReturnType<typeof blotterPanelItemDetailsSelected>;

export function blotterPanelPriceImprovementActionsAdded(item: IBlotterItem) {
  return { type: 'BLOTTER_PANEL_PRICEIMPROVEMENT_ACTIONS_ADDED' as const, item };
}
export type BlotterPanelPriceImprovementActionsAdded = ReturnType<typeof blotterPanelPriceImprovementActionsAdded>;

export function blotterPanelDownloadStatusUpdated(
  docType: DocumentType,
  language: string | undefined,
  format: DocumentFormat,
  status: BlotterPanelDocumentStatus,
) {
  return { type: 'BLOTTER_PANEL_DOWNLOAD_STATUS_UPDATED' as const, status, docType, language, format };
}
export type BlotterPanelDownloadStatusUpdated = ReturnType<typeof blotterPanelDownloadStatusUpdated>;
